import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";
import {
    OrderAttributes,
    OrderPageResponse,
    RefundStatus,
    OrderStatus,
    BaseOrder,
    ReturnReasonPayload,
    CancelReasonPayload,
} from "@models/order.model";
import { TableDataResponse } from "@models/data-grid.model";
import { PaymentMethod } from "@models/payment.model";
import { ExchangeReasonPayload } from "@models/order.model";
import { OrderItemAttributes } from "@models/orderItem.model";

@Injectable({
    providedIn: "root",
})
export class OrderService {
    constructor(private readonly http: HttpClient) {}

    public createOrder(orderData: BaseOrder, paymentMethod: PaymentMethod): Observable<OrderAttributes> {
        const data = { orderData, paymentMethod };
        return this.http.post(`${environment.apiUrlBasePath}/orders/create`, data) as Observable<OrderAttributes>;
    }

    public getOrderById(orderId: string): Observable<OrderAttributes> {
        return this.http.get<OrderAttributes>(`${environment.apiUrlBasePath}/orders/${orderId}`);
    }

    // To get order item data
    public getOrderItemById(orderId: string, itemID: string): Observable<OrderItemAttributes> {
        return this.http.get<OrderItemAttributes>(`${environment.apiUrlBasePath}/orders/trackById`, {
            params: { orderId, itemID },
        });
    }

    public getOrder(
        pageIndex: number = environment.dynamicPagination.pageIndex,
        pageSize: number = environment.dynamicPagination.pageSize,
    ): Observable<OrderPageResponse> {
        return this.http.get<OrderPageResponse>(
            `${environment.apiUrlBasePath}/orders/mobileNumber?pageIndex=${pageIndex}&pageSize=${pageSize}`,
        );
    }

    public sendHelpRequest(message: string, orderId: number): Observable<string> {
        const payload = { message, orderId };
        return this.http.post<string>(`${environment.apiUrlBasePath}/orders/helpRequest`, payload);
    }

    public addOrderStatus(params: {
        orderId: number;
        newStatus: OrderStatus;
        date: Date;
        time: string;
        awbNumber: string;
        courierName: string;
        utrNumber: string;
        itemIds: number[];
        trackingUrl: string;
    }): Observable<string> {
        return this.http.post<string>(`${environment.apiUrlBasePath}/admin/updateOrderStatus/`, params);
    }

    public getAllOrders(
        pageIndex: number = environment.dynamicPagination.pageIndex,
        pageSize: number = environment.dynamicPagination.pageSize,
        status?: string,
    ): Observable<TableDataResponse> {
        return this.http.get<TableDataResponse>(
            `${environment.apiUrlBasePath}/admin/allOrders?pageIndex=${pageIndex}&pageSize=${pageSize}&status=${status}`,
        );
    }

    public getAllStatus(): Observable<string[]> {
        return this.http.get<string[]>(`${environment.apiUrlBasePath}/admin/getAllStatus`);
    }

    public getOrderDetails(id: number): Observable<OrderAttributes> {
        return this.http.get<OrderAttributes>(`${environment.apiUrlBasePath}/admin/getOrderDetails/${id}`);
    }

    public cancelOrder(orderId: number): Observable<{ message: string }> {
        return this.http.put<{ message: string }>(`${environment.apiUrlBasePath}/orders/${orderId}/cancel`, {});
    }

    public returnOrder(
        orderId: number,
        reason: string,
        refundOption?: string,
        accountHolderName?: string,
        accountNumber?: string,
        ifscCode?: string,
        upiId?: string,
    ): Observable<{ message: string }> {
        const body: {
            reason: string;
            refundOption?: string;
            accountHolderName?: string;
            accountNumber?: string;
            ifscCode?: string;
            upiId?: string;
        } = { reason, refundOption, accountHolderName, accountNumber, ifscCode, upiId };
        return this.http.put<{ message: string }>(`${environment.apiUrlBasePath}/orders/${orderId}/return`, body);
    }

    public exchangeOrder(orderId: number, reason: string): Observable<{ message: string }> {
        const body: { reason: string } = { reason };
        return this.http.put<{ message: string }>(`${environment.apiUrlBasePath}/orders/${orderId}/exchange`, body);
    }

    public cancelSelectedItem(
        orderId: string,
        cancelReasonPayload: CancelReasonPayload[],
    ): Observable<{ message: string }> {
        const data = { cancelReasonPayload };
        return this.http.put<{ message: string }>(
            `${environment.apiUrlBasePath}/orders/${orderId}/cancelSelectedItem`,
            data,
        );
    }

    public returnSelectedItem(
        orderId: string,
        returnReasonPayload: ReturnReasonPayload[],
        refundOption?: string,
        accountHolderName?: string,
        accountNumber?: string,
        ifscCode?: string,
        upiId?: string,
    ): Observable<{ message: string }> {
        const data = { returnReasonPayload, refundOption, accountHolderName, accountNumber, ifscCode, upiId };
        return this.http.put<{ message: string }>(
            `${environment.apiUrlBasePath}/orders/${orderId}/returnSelectedItem`,
            data,
        );
    }

    public exchangeSelectedItem(
        orderId: string,
        exchangeReasonPayload: ExchangeReasonPayload[],
    ): Observable<{ message: string }> {
        const data = { exchangeReasonPayload };
        return this.http.put<{ message: string }>(
            `${environment.apiUrlBasePath}/orders/${orderId}/exchangeSelectedItem`,
            data,
        );
    }

    public getRecentDeliveredOrExchangedItemIds(): Observable<number[]> {
        return this.http.get<number[]>(`${environment.apiUrlBasePath}/orders/getRecentDeliveredOrExchangedItemIds`);
    }

    getRefundStatusClass(refundStatus: string | undefined): string {
        switch (refundStatus) {
            case RefundStatus.RefundInitiated:
                return "refund-initiated";
            case RefundStatus.RefundProcessed:
                return "refund-processed";
            case RefundStatus.RefundSuccessful:
                return "refund-successful";
            case RefundStatus.RefundFailed:
                return "refund-failed";
            default:
                return "";
        }
    }

    public checkIsFromCart(orderId: number): Observable<{ isFromCart: boolean }> {
        return this.http.get<{ isFromCart: boolean }>(
            `${environment.apiUrlBasePath}/orders/checkIsFromCart/${orderId}`,
        );
    }

    public isFirstOrder(): Observable<{ exists: boolean }> {
        return this.http.get<{ exists: boolean }>(`${environment.apiUrlBasePath}/orders/checkOrderExist`);
    }

    public getOrderItemsByStatus(orderId: string, status: string): Observable<OrderItemAttributes[]> {
        return this.http.get<OrderItemAttributes[]>(
            `${environment.apiUrlBasePath}/orders/${orderId}/items?status=${status}`,
        );
    }
}
